import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex align-center ga-3 mb-3" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h2", { class: "text-subtitle-1 font-weight-bold mb-2" }, "Datos personales", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h2", { class: "text-subtitle-1 font-weight-bold mt-2 mb-2" }, "Como jugador", -1)
const _hoisted_4 = { class: "d-flex flex-wrap ga-2 mb-3" }

import { reactive, ref } from "vue";
import { EditarUsuarioDTO, UsuarioViewDTO } from "@/interfaces/Usuario";
import { FaccionDTO } from "@/interfaces/Faccion";
import { editarUsuario } from "@/services/UsuariosService";
import { useToast } from "@/composables/useToast";
import { fechaConocida } from "@/utils/Fecha";
import avatarPorDefecto from "@/assets/icons/perfil.png";

const LADO_FOTO = 256;

export default /*#__PURE__*/_defineComponent({
  __name: 'FormularioPerfil',
  props: {
    usuario: {},
    facciones: {}
  },
  emits: ["guardado", "cancelar"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { triggerToast } = useToast();
const formulario = ref<{ validate: () => Promise<{ valid: boolean }> }>();
const selectorFoto = ref<HTMLInputElement>();
const guardando = ref(false);

// Una copia: lo que se escribe no toca el perfil hasta guardar
const datos = reactive({
  nombreUsuario: props.usuario.nombreUsuario ?? "",
  primerApellido: props.usuario.primerApellido ?? "",
  segundoApellido: props.usuario.segundoApellido ?? "",
  fechaNacimiento: fechaConocida(props.usuario.fechaNacimiento),
  telefono: props.usuario.telefono ?? "",
  ciudad: props.usuario.ciudad ?? "",
  nick: props.usuario.nick ?? "",
  idFaccion: props.usuario.idFaccion && props.usuario.idFaccion > 0 ? props.usuario.idFaccion : undefined,
  nickLGDA: props.usuario.nickLGDA ?? "",
  imagen: props.usuario.imagen ?? "",
});

const obligatorio = (valor: string) => !!valor?.trim() || "Obligatorio";
const telefonoValido = (valor: string) =>
  !valor || /^\d{9}$/.test(valor) || "Tienen que ser 9 cifras";

// La foto se reduce antes de guardarla: una de la cámara pesa megas y solo se pinta pequeña
const reducirFoto = (archivo: File) =>
  new Promise<string>((resolve, reject) => {
    const url = URL.createObjectURL(archivo);
    const imagen = new Image();
    imagen.onload = () => {
      const escala = Math.min(1, LADO_FOTO / Math.max(imagen.width, imagen.height));
      const lienzo = document.createElement("canvas");
      lienzo.width = Math.round(imagen.width * escala);
      lienzo.height = Math.round(imagen.height * escala);
      lienzo.getContext("2d")?.drawImage(imagen, 0, 0, lienzo.width, lienzo.height);
      URL.revokeObjectURL(url);
      resolve(lienzo.toDataURL("image/jpeg", 0.85));
    };
    imagen.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo leer la imagen"));
    };
    imagen.src = url;
  });

const cambiarFoto = async (evento: Event) => {
  const entrada = evento.target as HTMLInputElement;
  const archivo = entrada.files?.[0];
  entrada.value = "";
  if (!archivo) return;
  try {
    datos.imagen = await reducirFoto(archivo);
  } catch {
    triggerToast("error", "No se pudo leer la imagen");
  }
};

const guardar = async () => {
  if (!(await formulario.value?.validate())?.valid) return;

  const cambios: Partial<UsuarioViewDTO> = {
    nombreUsuario: datos.nombreUsuario.trim(),
    primerApellido: datos.primerApellido.trim(),
    segundoApellido: datos.segundoApellido.trim(),
    telefono: datos.telefono.trim(),
    ciudad: datos.ciudad.trim(),
    nick: datos.nick.trim(),
    nickLGDA: datos.nickLGDA.trim(),
    // Vacío no se manda: el back dejaría la que había
    ...(datos.fechaNacimiento ? { fechaNacimiento: datos.fechaNacimiento } : {}),
    ...(datos.idFaccion ? { idFaccion: datos.idFaccion } : {}),
    // La foto solo si ha cambiado: es lo que más pesa
    ...(datos.imagen !== (props.usuario.imagen ?? "") ? { imagen: datos.imagen } : {}),
  };
  const peticion: EditarUsuarioDTO = { idUsuario: props.usuario.idUsuario, ...cambios };

  guardando.value = true;
  try {
    await editarUsuario(peticion);
    triggerToast("success", "Perfil actualizado");
    emit("guardado", cambios);
  } catch (error) {
    console.error("Error al editar el perfil:", error);
    triggerToast("error", "No se pudo guardar el perfil");
  } finally {
    guardando.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    ref_key: "formulario",
    ref: formulario,
    onSubmit: _withModifiers(guardar, ["prevent"])
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        variant: "outlined",
        class: "pa-3 mb-3"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_avatar, { size: "64" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_img, {
                  src: datos.imagen || _unref(avatarPorDefecto),
                  alt: "Avatar",
                  cover: ""
                }, null, 8, ["src"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_btn, {
              variant: "tonal",
              size: "small",
              "prepend-icon": "mdi-camera",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (selectorFoto.value?.click()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Cambiar foto ")
              ]),
              _: 1
            }),
            _createElementVNode("input", {
              ref_key: "selectorFoto",
              ref: selectorFoto,
              type: "file",
              accept: "image/*",
              hidden: "",
              onChange: cambiarFoto
            }, null, 544)
          ]),
          _hoisted_2,
          _createVNode(_component_v_text_field, {
            modelValue: datos.nombreUsuario,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((datos.nombreUsuario) = $event)),
            label: "Nombre",
            rules: [obligatorio],
            maxlength: "50",
            density: "comfortable"
          }, null, 8, ["modelValue", "rules"]),
          _createVNode(_component_v_text_field, {
            modelValue: datos.primerApellido,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((datos.primerApellido) = $event)),
            label: "Primer apellido",
            rules: [obligatorio],
            maxlength: "50",
            density: "comfortable"
          }, null, 8, ["modelValue", "rules"]),
          _createVNode(_component_v_text_field, {
            modelValue: datos.segundoApellido,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((datos.segundoApellido) = $event)),
            label: "Segundo apellido",
            maxlength: "50",
            density: "comfortable"
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_text_field, {
            modelValue: datos.fechaNacimiento,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((datos.fechaNacimiento) = $event)),
            label: "Fecha de nacimiento",
            type: "date",
            density: "comfortable"
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_text_field, {
            modelValue: datos.telefono,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((datos.telefono) = $event)),
            label: "Teléfono",
            type: "tel",
            inputmode: "numeric",
            rules: [telefonoValido],
            maxlength: "9",
            density: "comfortable"
          }, null, 8, ["modelValue", "rules"]),
          _createVNode(_component_v_text_field, {
            modelValue: datos.ciudad,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((datos.ciudad) = $event)),
            label: "Ciudad",
            maxlength: "100",
            density: "comfortable"
          }, null, 8, ["modelValue"]),
          _hoisted_3,
          _createVNode(_component_v_text_field, {
            modelValue: datos.nick,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((datos.nick) = $event)),
            label: "Nick",
            rules: [obligatorio],
            maxlength: "50",
            density: "comfortable"
          }, null, 8, ["modelValue", "rules"]),
          _createVNode(_component_v_autocomplete, {
            modelValue: datos.idFaccion,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((datos.idFaccion) = $event)),
            items: _ctx.facciones,
            "item-title": "nombreFaccion",
            "item-value": "idFaccion",
            label: "Comunidad",
            density: "comfortable"
          }, null, 8, ["modelValue", "items"]),
          _createVNode(_component_v_text_field, {
            modelValue: datos.nickLGDA,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((datos.nickLGDA) = $event)),
            label: "Nick en La Guerra del Anillo",
            maxlength: "50",
            density: "comfortable",
            "hide-details": ""
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_v_btn, {
          type: "submit",
          color: "primary",
          loading: guardando.value,
          class: "flex-grow-1"
        }, {
          default: _withCtx(() => [
            _createTextVNode("Guardar")
          ]),
          _: 1
        }, 8, ["loading"]),
        _createVNode(_component_v_btn, {
          variant: "tonal",
          disabled: guardando.value,
          class: "flex-grow-1",
          onClick: _cache[10] || (_cache[10] = ($event: any) => (emit('cancelar')))
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Cancelar ")
          ]),
          _: 1
        }, 8, ["disabled"])
      ])
    ]),
    _: 1
  }, 512))
}
}

})